import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Tag, Button, Space, message, Modal, Form, Input, ColorPicker, Popconfirm, Typography, Select, Switch } from 'antd';
import { BookOutlined, PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { adminApi } from '../../../api/client';

const { Text } = Typography;

export function DictionariesTab() {
    const queryClient = useQueryClient();
    const [isAddDictModalOpen, setIsAddDictModalOpen] = useState(false);
    const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
    const [selectedDict, setSelectedDict] = useState<any>(null);
    const [dictForm] = Form.useForm();
    const [itemForm] = Form.useForm();

    const { data: dictionaries, isLoading } = useQuery({
        queryKey: ['dictionaries'],
        queryFn: async () => (await adminApi.getDictionaries()).data,
    });

    const addItemMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => adminApi.addDictionaryItem(id, data),
        onSuccess: () => {
            message.success('Element dodany');
            queryClient.invalidateQueries({ queryKey: ['dictionaries'] });
            setIsAddItemModalOpen(false);
            itemForm.resetFields();
        },
    });

    const deleteItemMutation = useMutation({
        mutationFn: (itemId: string) => adminApi.deleteDictionaryItem(itemId),
        onSuccess: (response: any) => {
            const messageText = response?.data?.message || 'Element zaktualizowany';
            if (response?.data?.action === 'deactivated') {
                message.warning(messageText, 5);
            } else {
                message.success(messageText, 5);
            }
            queryClient.invalidateQueries({ queryKey: ['dictionaries'] });
            queryClient.invalidateQueries({ queryKey: ['admin-dictionaries'] });
        },
    });

    const [editingItem, setEditingItem] = useState<any>(null);

    const updateItemMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => adminApi.updateDictionaryItem(id, data),
        onSuccess: () => {
            message.success('Zaktualizowano element');
            queryClient.invalidateQueries({ queryKey: ['admin-dictionaries'] });
            queryClient.invalidateQueries({ queryKey: ['dictionaries'] });
            setIsAddItemModalOpen(false);
            setEditingItem(null);
            itemForm.resetFields();
        },
        onError: () => message.error('Błąd podczas aktualizacji elementu'),
    });

    const handleCreateDictionary = async () => {
        // Disabled
    };

    const handleMoveItem = (items: any[], index: number, direction: 'up' | 'down') => {
        const currentItem = items[index];
        const swapItem = direction === 'up' ? items[index - 1] : items[index + 1];
        if (!swapItem) return;

        const currentSort = currentItem.sortOrder;
        const swapSort = swapItem.sortOrder;

        updateItemMutation.mutate({ id: currentItem.id, data: { sortOrder: swapSort } });
        updateItemMutation.mutate({ id: swapItem.id, data: { sortOrder: currentSort } });
    };

    const handleAddItem = async () => {
        if (!selectedDict) return;
        const values = await itemForm.validateFields();
        const { financialType, ...restValues } = values;
        const data = {
            ...restValues,
            color: typeof values.color === 'string' ? values.color : values.color?.toHexString?.() || null,
            metadata: financialType ? { financialType } : null,
        };
        if (editingItem) {
            updateItemMutation.mutate({ id: editingItem.id, data });
        } else {
            addItemMutation.mutate({ id: selectedDict.id, data });
        }
    };

    return (
        <div>
            <Card
                title={<Space><BookOutlined /> Słowniki systemowe</Space>}
                loading={isLoading}
            >
                <Table
                    dataSource={dictionaries || []}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    columns={[
                        { title: 'Słownik', dataIndex: 'name', key: 'name', render: (name: string) => <Text strong>{name}</Text> },
                        { title: 'Kod', dataIndex: 'code', key: 'code', render: (code: string) => <Tag>{code}</Tag> },
                        { title: 'Opis', dataIndex: 'description', key: 'description' },
                        { title: 'Elementy', dataIndex: 'items', key: 'items', render: (items: any[]) => items?.length || 0 },
                        {
                            title: 'Akcje', key: 'actions', render: (_: any, record: any) => (
                                <Space>
                                    <Button size="small" icon={<PlusOutlined />} onClick={() => { setSelectedDict(record); setIsAddItemModalOpen(true); }}>
                                        Dodaj element
                                    </Button>
                                </Space>
                            )
                        },
                    ]}
                    expandable={{
                        expandedRowRender: (record) => (
                            <Table
                                dataSource={record.items || []}
                                rowKey={(item, idx) => `${record.id}-${idx}`}
                                pagination={false}
                                size="small"
                                columns={[
                                    { title: 'Kod', dataIndex: 'code', key: 'code', render: (code: string) => <Tag>{code}</Tag> },
                                    { title: 'Nazwa', dataIndex: 'name', key: 'name' },
                                    { title: 'Opis', dataIndex: 'description', key: 'description' },
                                    { title: 'Kolor', dataIndex: 'color', key: 'color', render: (color: string) => color ? <Tag color={color}>{color}</Tag> : '-' },
                                    { title: 'Typ (Finanse)', key: 'financialType', render: (_: any, itemRecord: any) => itemRecord.metadata?.financialType ? <Tag color="blue">{itemRecord.metadata.financialType}</Tag> : null },
                                    {
                                        title: 'Kolejność', dataIndex: 'sortOrder', key: 'sortOrder', render: (sortOrder: number, itemRecord: any, index: number) => (
                                            <Space>
                                                {sortOrder}
                                                <Button size="small" type="text" onClick={() => handleMoveItem(record.items, index, 'up')} disabled={index === 0}>▲</Button>
                                                <Button size="small" type="text" onClick={() => handleMoveItem(record.items, index, 'down')} disabled={index === record.items.length - 1}>▼</Button>
                                            </Space>
                                        )
                                    },
                                    { title: 'Aktywny', dataIndex: 'isActive', key: 'isActive', render: (val: boolean) => val !== false ? <Tag color="green">Tak</Tag> : <Tag color="red">Nie</Tag> },
                                    {
                                        title: 'Akcje', key: 'actions', render: (_: any, itemRecord: any) => (
                                            <Space>
                                                <Button size="small" icon={<EditOutlined />} onClick={() => {
                                                    setEditingItem(itemRecord);
                                                    setSelectedDict(record);
                                                    itemForm.setFieldsValue({
                                                        ...itemRecord,
                                                        financialType: itemRecord.metadata?.financialType
                                                    });
                                                    setIsAddItemModalOpen(true);
                                                }} />
                                                <Popconfirm title="Czy na pewno chcesz usunąć ten element?" onConfirm={() => deleteItemMutation.mutate(itemRecord.id)}>
                                                    <Button size="small" danger icon={<DeleteOutlined />} />
                                                </Popconfirm>
                                            </Space>
                                        )
                                    }
                                ]}
                            />
                        ),
                    }}
                />
            </Card>

            {/* Modal for adding/editing item to Dictionary */}
            <Modal
                title={editingItem ? `Edytuj element: ${editingItem.name}` : `Dodaj element do: ${selectedDict?.name}`}
                open={isAddItemModalOpen}
                onCancel={() => { setIsAddItemModalOpen(false); itemForm.resetFields(); setEditingItem(null); }}
                onOk={handleAddItem}
                confirmLoading={addItemMutation.isPending || updateItemMutation.isPending}
            >
                <Form form={itemForm} layout="vertical">
                    <Form.Item name="code" label="Kod" rules={[{ required: true }]}>
                        <Input placeholder="np. ORGANIC_SEARCH" />
                    </Form.Item>
                    <Form.Item name="name" label="Nazwa" rules={[{ required: true }]}>
                        <Input placeholder="np. Wyszukiwarka organiczna" />
                    </Form.Item>
                    <Form.Item name="description" label="Opis">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item name="color" label="Kolor">
                        <ColorPicker />
                    </Form.Item>
                    {selectedDict?.code === 'FINANCIAL_CATEGORY' && (
                        <Form.Item name="financialType" label="Typ wpisu finansowego">
                            <Select allowClear placeholder="Wybierz typ wpisu">
                                <Select.Option value="EXPENSE">Koszt (EXPENSE)</Select.Option>
                                <Select.Option value="REVENUE">Przychód (REVENUE)</Select.Option>
                                <Select.Option value="TRANSFER">Transfer (TRANSFER)</Select.Option>
                            </Select>
                        </Form.Item>
                    )}
                    {editingItem && (
                        <Form.Item name="isActive" label="Aktywny" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </div>
    );
}
